/**
 * Feature-info retrieval.
 *
 * GeoServer WMS  → GetFeatureInfo ─┐
 *                                  ├─→ FeatureQueryResult → shared popup
 * ArcGIS REST    → identify/query ─┘
 *
 * ONLY the retrieval mechanism differs between sources. Both paths produce the
 * same `FeatureQueryResult` shape, which the popup renders identically — the
 * user cannot tell which source a feature came from.
 */

import TileWMSSource from 'ol/source/TileWMS';
import type { Coordinate } from 'ol/coordinate';
import { esriGeometryToGeoJson, withArcgisToken } from '../utils/arcgis';

export type FeatureSourceType = 'geoserver' | 'esri';

/** Which toolbar actions the underlying service actually supports. */
export interface ServiceCapabilities {
  edit: boolean;
  related: boolean;
  attachments: boolean;
  attachmentCount: number;
}

export interface FeatureQueryResult {
  layerTitle: string;
  sourceType: FeatureSourceType;
  /** Display-ready values (coded values resolved, dates formatted). */
  attributes: Record<string, unknown>;
  /** Raw field name → alias, when the service publishes them. */
  aliases: Record<string, string>;
  capabilities: ServiceCapabilities;
  /**
   * The feature's own shape, in WGS84, when the source returned one.
   *
   * Absent for GeoServer's HTML and plain-text fallbacks, which carry no
   * geometry at all — so anything built on this (zoom-to, highlighting) must
   * degrade quietly rather than assume it is there.
   */
  geometry?: GeoJSON.Geometry;
}

const NO_CAPABILITIES: ServiceCapabilities = {
  edit: false,
  related: false,
  attachments: false,
  attachmentCount: 0,
};

/* ────────────────────────────── GeoServer WMS ───────────────────────────── */

/** Parses GeoServer's `text/html` GetFeatureInfo table into attributes. */
function parseHtmlFeatureInfo(html: string): Record<string, unknown>[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const tables = Array.from(doc.querySelectorAll('table'));
  const features: Record<string, unknown>[] = [];

  for (const table of tables) {
    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length < 2) continue;

    const headers = Array.from(rows[0].querySelectorAll('th, td')).map((cell) =>
      (cell.textContent ?? '').trim(),
    );
    for (const row of rows.slice(1)) {
      const cells = Array.from(row.querySelectorAll('td')).map((cell) =>
        (cell.textContent ?? '').trim(),
      );
      if (cells.length === 0) continue;
      const attributes: Record<string, unknown> = {};
      headers.forEach((header, index) => {
        if (header) attributes[header] = cells[index] ?? null;
      });
      if (Object.keys(attributes).length > 0) features.push(attributes);
    }
  }
  return features;
}

/** Parses GeoServer's `text/plain` output ("field = value" lines). */
function parseTextFeatureInfo(text: string): Record<string, unknown>[] {
  const features: Record<string, unknown>[] = [];
  let current: Record<string, unknown> | null = null;

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    if (/^Layer/i.test(line) || /^-+$/.test(line)) {
      if (current && Object.keys(current).length > 0) features.push(current);
      current = {};
      continue;
    }
    const separator = line.indexOf('=');
    if (separator > 0) {
      if (!current) current = {};
      current[line.slice(0, separator).trim()] = line.slice(separator + 1).trim();
    }
  }
  if (current && Object.keys(current).length > 0) features.push(current);
  return features;
}

/**
 * Queries one WMS layer. Tries JSON, then HTML, then plain text — some
 * GeoServer layers are not configured for `application/json`.
 *
 * OL's TileWMS source already knows how to build a correct GetFeatureInfo URL
 * from a coordinate, resolution and projection, so there's no hand-rolled
 * bbox/pixel math and no need to try more than one WMS version — OL built
 * the request itself, so its own (1.3.0) default is enough.
 */
export async function queryGeoServerWmsOL(
  wmsBaseUrl: string,
  layerName: string,
  coordinate: Coordinate,
  resolution: number,
): Promise<FeatureQueryResult[]> {
  const source = new TileWMSSource({ url: wmsBaseUrl, params: { LAYERS: layerName } });
  const attempts = ['application/json', 'text/html', 'text/plain'];

  let anyRequestSucceeded = false;

  for (const infoFormat of attempts) {
    const url = source.getFeatureInfoUrl(coordinate, resolution, 'EPSG:3857', {
      INFO_FORMAT: infoFormat,
      QUERY_LAYERS: layerName,
      FEATURE_COUNT: '10',
      BUFFER: '8',
    });
    if (!url) continue;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(
          `[featureInfo] GetFeatureInfo failed (${response.status}) layer="${layerName}" format="${infoFormat}"\n  ${url}`,
        );
        continue;
      }
      anyRequestSucceeded = true;

      const body = await response.text();
      let attributeSets: Record<string, unknown>[] = [];
      let geometries: (GeoJSON.Geometry | undefined)[] = [];

      if (infoFormat === 'application/json') {
        try {
          const data = JSON.parse(body);
          const usable = (data?.features ?? []).filter(
            (feature: any) => Object.keys(feature?.properties ?? {}).length > 0,
          );
          attributeSets = usable.map((feature: any) => feature.properties);
          geometries = usable.map((feature: any) => feature?.geometry ?? undefined);
        } catch {
          continue;
        }
      } else if (infoFormat === 'text/html') {
        attributeSets = parseHtmlFeatureInfo(body);
      } else {
        attributeSets = parseTextFeatureInfo(body);
      }

      if (attributeSets.length === 0) continue;

      return attributeSets.map((attributes, index) => ({
        layerTitle: layerName,
        sourceType: 'geoserver' as const,
        attributes,
        aliases: {},
        capabilities: NO_CAPABILITIES,
        geometry: geometries[index],
      }));
    } catch (error) {
      console.error(
        `[featureInfo] GetFeatureInfo request error layer="${layerName}" format="${infoFormat}" — likely CORS or network.\n  ${url}\n`,
        error,
      );
    }
  }

  if (!anyRequestSucceeded) {
    throw new Error(`GetFeatureInfo failed for layer "${layerName}"`);
  }

  return [];
}

/* ────────────────────────────── ArcGIS REST ─────────────────────────────── */

interface EsriFieldDef {
  name: string;
  alias?: string;
  type?: string;
  domain?: { codedValues?: { code: unknown; name: string }[] } | null;
}

/** Resolves coded-value domains and epoch-millisecond dates to display text. */
function applyFieldDefinitions(
  attributes: Record<string, unknown>,
  fields: EsriFieldDef[],
): { attributes: Record<string, unknown>; aliases: Record<string, string> } {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const resolved: Record<string, unknown> = {};
  const aliases: Record<string, string> = {};

  for (const [name, value] of Object.entries(attributes)) {
    const definition = byName.get(name);
    if (definition?.alias) aliases[name] = definition.alias;

    if (value === null || value === undefined) {
      resolved[name] = value;
      continue;
    }

    // Coded-value domain: show the human label, not the stored code.
    const coded = definition?.domain?.codedValues?.find((entry) => entry.code === value);
    if (coded) {
      resolved[name] = coded.name;
      continue;
    }

    // Esri dates are epoch milliseconds.
    if (definition?.type === 'esriFieldTypeDate' && typeof value === 'number') {
      const date = new Date(value);
      resolved[name] = Number.isNaN(date.getTime()) ? value : date.toLocaleString();
      continue;
    }

    resolved[name] = value;
  }

  return { attributes: resolved, aliases };
}

function readCapabilities(metadata: any): ServiceCapabilities {
  const capabilityList = String(metadata?.capabilities ?? '').toLowerCase();
  return {
    edit:
      capabilityList.includes('update') ||
      capabilityList.includes('create') ||
      capabilityList.includes('editing'),
    related: Array.isArray(metadata?.relationships) && metadata.relationships.length > 0,
    attachments: Boolean(metadata?.hasAttachments),
    attachmentCount: 0,
  };
}

/**
 * Queries an ArcGIS layer.
 * - FeatureServer → `/{layerId}/query` (identify is not supported there)
 * - MapServer     → `/{layerId}/query` too, which keeps field defs available
 *   for aliases and coded values (plain `identify` returns pre-aliased keys
 *   but no domain/type info).
 *
 * ~8px tolerance so points are clickable without pixel-perfect aim, in real
 * ground metres: OL's view resolution is in projected (EPSG:3857) metres per
 * pixel, and ground distance = projected distance × cos(latitude) is Web
 * Mercator's own scale factor.
 */
export async function queryEsriRestOL(
  serviceBaseUrl: string,
  layerId: number,
  lon: number,
  lat: number,
  /** The OL view's resolution (projected metres/pixel) at the click. */
  resolution: number,
  /** The unstripped `arcgisUrl` — see ArcgisFeatureLayer's own doc on this
   *  same idea in MapViewOL.tsx. `serviceBaseUrl` above never carries a
   *  token, since callers append `/${layerId}` after it. */
  rawArcgisUrl?: string,
): Promise<FeatureQueryResult[]> {
  const groundMetresPerPixel = resolution * Math.cos((lat * Math.PI) / 180);
  const tolerance = Math.max(groundMetresPerPixel * 8, 1);
  return runEsriQuery(serviceBaseUrl, layerId, lon, lat, tolerance, rawArcgisUrl);
}

async function runEsriQuery(
  serviceBaseUrl: string,
  layerId: number,
  lon: number,
  lat: number,
  tolerance: number,
  rawArcgisUrl?: string,
): Promise<FeatureQueryResult[]> {
  const layerUrl = `${serviceBaseUrl}/${layerId}`;

  const params = new URLSearchParams({
    geometry: `${lon},${lat}`,
    geometryType: 'esriGeometryPoint',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    distance: String(Math.round(tolerance)),
    units: 'esriSRUnit_Meter',
    outFields: '*',
    /* Asked for so the pop-up can zoom to the feature and highlight it. The
       response is Esri JSON, not GeoJSON, because the field definitions that
       resolve coded domains and aliases only come back in that format. */
    returnGeometry: 'true',
    resultRecordCount: '10',
    f: 'json',
  });

  const queryUrl = withArcgisToken(`${layerUrl}/query?${params.toString()}`, rawArcgisUrl);

  try {
    // Metadata gives aliases, field types, coded domains, and capabilities.
    const [queryResponse, metadataResponse] = await Promise.all([
      fetch(queryUrl),
      fetch(withArcgisToken(`${layerUrl}?f=json`, rawArcgisUrl)),
    ]);

    if (!queryResponse.ok) {
      console.error(
        `[featureInfo] ArcGIS query failed (${queryResponse.status}) layer=${layerId}\n  ${queryUrl}`,
      );
      return [];
    }

    const data = await queryResponse.json();
    // ArcGIS reports failures as HTTP 200 with an `error` body.
    if (data?.error) {
      console.error(
        `[featureInfo] ArcGIS returned an error for layer=${layerId}: ${data.error.message}\n  ${queryUrl}`,
      );
      return [];
    }

    const features: any[] = data?.features ?? [];
    if (features.length === 0) return [];

    let metadata: any = null;
    if (metadataResponse.ok) {
      metadata = await metadataResponse.json().catch(() => null);
    }

    const fieldDefs: EsriFieldDef[] = metadata?.fields ?? data?.fields ?? [];
    const layerTitle = metadata?.name || `Layer ${layerId}`;
    const capabilities = readCapabilities(metadata);

    return features.map((feature) => {
      const { attributes, aliases } = applyFieldDefinitions(feature?.attributes ?? {}, fieldDefs);
      return {
        layerTitle,
        sourceType: 'esri' as const,
        attributes,
        aliases,
        capabilities,
        geometry: esriGeometryToGeoJson(feature?.geometry),
      };
    });
  } catch (error) {
    console.error(
      `[featureInfo] ArcGIS request error layer=${layerId} — likely CORS or network.\n  ${queryUrl}\n`,
      error,
    );
    return [];
  }
}
